/**
 * ManageDivisionGamesPage — admin-only page to edit, delete, and abandon games
 * in a specific division of an ACTIVE season. Shows a read-only notice for
 * completed seasons.
 *
 * Route: /leagues/:leagueId/seasons/:seasonId/divisions/:divisionId/games/manage
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import { fetchGames, cancelGame, resetGameResult } from '../../store/slices/gameSlice';
import { fetchLeagueSeason } from '../../store/slices/leagueSlice';
import { GameStatus, UserRole } from '@sulis-liiga/common';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';
import { teamLabelsOf } from '../../utils/gameTeams';

// ─── ManageDivisionGamesPage ──────────────────────────────────────────────────

const ManageDivisionGamesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { leagueId, seasonId, divisionId } = useParams<{
    leagueId: string;
    seasonId: string;
    divisionId: string;
  }>();

  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const {
    games,
    loading: gameLoading,
    error: gameError,
  } = useSelector((state: RootState) => state.game);
  const { currentSeason } = useSelector((state: RootState) => state.league);

  const isAdmin =
    isAuthenticated &&
    (user?.roles?.includes(UserRole.LEAGUE_ADMINISTRATOR) ||
      user?.roles?.includes(UserRole.SUPER_ADMINISTRATOR));

  const { resolved: tenantResolved } = useActiveLeagueTenant(leagueId);

  const [resetConfirmId, setResetConfirmId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // "Edit" opens the shared Edit-a-Game page; it returns here when done/cancelled.
  const managePath =
    leagueId && seasonId && divisionId
      ? `/leagues/${leagueId}/seasons/${seasonId}/divisions/${divisionId}/games/manage`
      : '';
  const goToEdit = (gameId: string) =>
    navigate(`/games/${gameId}/edit?returnTo=${encodeURIComponent(managePath)}`);

  const loadGames = useCallback(() => {
    if (divisionId && tenantResolved) {
      dispatch(fetchGames({ divisionId }));
    }
  }, [dispatch, divisionId, tenantResolved]);

  useEffect(() => {
    if (leagueId && seasonId && tenantResolved) {
      dispatch(fetchLeagueSeason({ leagueId, seasonId }));
    }
  }, [dispatch, leagueId, seasonId, tenantResolved]);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  const isSeasonActive = currentSeason?.status === 'active';
  const isSeasonCompleted = currentSeason?.status === 'completed';

  const handleAbandon = async (gameId: string) => {
    setActionError(null);
    const result = await dispatch(cancelGame({ gameId, reason: 'abandoned' }));
    if (cancelGame.rejected.match(result)) {
      setActionError((result.payload as string) || 'Failed to mark as abandoned.');
    }
  };

  const handleReset = async (gameId: string) => {
    setActionError(null);
    const result = await dispatch(resetGameResult(gameId));
    if (resetGameResult.rejected.match(result)) {
      setActionError((result.payload as string) || 'Failed to reset game result.');
    }
    setResetConfirmId(null);
  };

  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto mt-16 text-center">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access denied</h2>
          <p className="text-gray-600">You must be a League Administrator to manage games.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Back link */}
      {leagueId && seasonId && (
        <Link
          to={`/leagues/${leagueId}/divisions`}
          className="text-sm text-primary-600 hover:underline"
        >
          &larr; Back to Divisions
        </Link>
      )}

      {/* Page header */}
      <div className="mt-2 pb-4 border-b border-gray-200 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Division Games</h1>
          {currentSeason && (
            <p className="text-sm text-gray-500 mt-1">Season: {currentSeason.name}</p>
          )}
        </div>
        <span className="text-xs px-2 py-1 rounded-full bg-violet-600 text-white">Admin view</span>
      </div>

      {/* Season completed notice */}
      {isSeasonCompleted && (
        <div className="mt-4 rounded-md bg-gray-50 border border-gray-200 p-4">
          <p className="text-sm text-gray-600">Games in a completed season cannot be modified.</p>
        </div>
      )}

      {actionError && (
        <div className="mt-4 rounded-md bg-danger-50 p-3" role="alert">
          <p className="text-sm text-danger-600">{actionError}</p>
        </div>
      )}

      {gameError && (
        <div className="mt-4 rounded-md bg-danger-50 p-3" role="alert">
          <p className="text-sm text-danger-600">{gameError}</p>
        </div>
      )}

      {gameLoading && (
        <div className="flex items-center justify-center py-12">
          <svg
            className="animate-spin h-8 w-8 text-primary-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-label="Loading"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      )}

      {!gameLoading && (
        <div className="mt-4 space-y-3">
          {games.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6 text-center text-gray-500 text-sm">
              No games in this division yet.
            </div>
          )}
          {games.map(game => {
            const [teamALabel, teamBLabel] = teamLabelsOf(game);
            const isResetConfirm = resetConfirmId === game.id;
            const isCompleted = game.status === GameStatus.COMPLETED;
            const isCancelled =
              game.status === GameStatus.CANCELLED || game.status === GameStatus.COMPLETED;
            const statusLabel =
              game.status === GameStatus.CANCELLED && game.cancellationReason === 'abandoned'
                ? 'Abandoned'
                : game.status;

            return (
              <div
                key={game.id}
                className="bg-white rounded-lg shadow-sm border border-gray-100 p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {teamALabel} <span className="text-gray-400">vs</span> {teamBLabel}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {game.scheduledDate
                        ? new Date(game.scheduledDate).toLocaleDateString()
                        : 'No date'}{' '}
                      {game.scheduledTime ? `· ${game.scheduledTime}` : ''}
                      {game.venue?.name ? ` · ${game.venue.name}` : ''}
                    </p>
                    <span
                      className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        game.status === GameStatus.COMPLETED
                          ? 'bg-green-100 text-green-800'
                          : game.status === GameStatus.CANCELLED
                          ? game.cancellationReason === 'abandoned'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-gray-100 text-gray-600'
                          : game.status === GameStatus.DISPUTED
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {statusLabel}
                    </span>
                  </div>

                  {isSeasonActive && !isCancelled && (
                    <div className="ml-4 flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => goToEdit(game.id)}
                        className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAbandon(game.id)}
                        disabled={gameLoading}
                        className="px-2.5 py-1 text-xs font-medium rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                      >
                        Mark abandoned
                      </button>
                    </div>
                  )}

                  {isSeasonActive && isCompleted && (
                    <div className="ml-4 flex items-center gap-2 shrink-0">
                      {!isResetConfirm && (
                        <button
                          type="button"
                          onClick={() => {
                            setResetConfirmId(game.id);
                            setActionError(null);
                          }}
                          className="px-2.5 py-1 text-xs font-medium rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50"
                        >
                          Reset result
                        </button>
                      )}
                      {isResetConfirm && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-amber-700 font-medium">
                            Reset result &amp; standings?
                          </span>
                          <button
                            type="button"
                            onClick={() => handleReset(game.id)}
                            disabled={gameLoading}
                            className="px-2.5 py-1 text-xs font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => setResetConfirmId(null)}
                            className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ManageDivisionGamesPage;
